import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { MouseEvent, useMemo } from 'react';
import pushRoute from '@utils/pushRoute';
import useUpscCurrentAffairsTicker, {
  UpscTickerItem
} from '@api/hooks/upscCurrentAffairs/useUpscCurrentAffairsTicker';

const TICKER_HEIGHT = '36px';
const TICKER_NAVY = '#0f2046';
const TICKER_LIVE_GREEN = '#3db267';

const Ticker = styled.div`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 100%;
  height: ${TICKER_HEIGHT};
  background: ${TICKER_NAVY};
  overflow: hidden;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const TickerLabel = styled.div`
  flex-shrink: 0;
  background: ${TICKER_LIVE_GREEN};
  color: var(--color-i-white);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 0 10px;
  height: 100%;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const TickerViewport = styled.div`
  overflow: hidden;
  flex: 1;
`;

const upscTickerScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${upscTickerScroll} 32s linear infinite;
  white-space: nowrap;

  &:hover {
    animation-play-state: paused;
  }
`;

const TickerItem = styled.a`
  padding: 0 28px;
  font-size: 12px;
  line-height: ${TICKER_HEIGHT};
  color: var(--color-text-secondary-light);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: var(--color-i-white);
  }
`;

const handleTickerLinkClick = (
  event: MouseEvent<HTMLAnchorElement>,
  link?: string
) => {
  if (!link || link === '#') {
    event.preventDefault();
    return;
  }

  if (link.startsWith('http://') || link.startsWith('https://')) {
    return;
  }

  event.preventDefault();
  pushRoute(link, link);
};

const UpscCurrentAffairsNewsTicker = () => {
  const { items, isLoading } = useUpscCurrentAffairsTicker();

  const tickerItems = useMemo((): UpscTickerItem[] => {
    if (!items.length) {
      return [];
    }
    return [...items, ...items];
  }, [items]);

  if (isLoading || !items.length) {
    return null;
  }

  return (
    <Ticker aria-label="UPSC Current Affairs announcements">
      <TickerLabel>LIVE</TickerLabel>
      <TickerViewport>
        <TickerTrack>
          {tickerItems.map((item, index) => (
            <TickerItem
              key={`${item.heading}-${index}`}
              href={item.link || '#'}
              onClick={(event) => handleTickerLinkClick(event, item.link)}
            >
              {item.heading}
            </TickerItem>
          ))}
        </TickerTrack>
      </TickerViewport>
    </Ticker>
  );
};

export default UpscCurrentAffairsNewsTicker;
